<template>
  <div
    :style="{
      willChange: 'transform',
      transform: 'translate3d(0, 0, 0)',
    }"
  >
    <div class="templates" :class="isVertical ? 'vertical' : ''">
      <div v-for="(item, idx) in computedList" :key="idx" class="template-item">
        <template v-if="!!item">
          <CardItem
            :data="item"
            :key="item.id"
            :auto-scale="true"
            :source="template_source"
            :mode="mode"
            @change="onChange($event, item)"
            @cover-click="onHandleCoverClick(item)"
            @preview-click="onHandlePreviewClick(item)"
          >
            {{ item.name }}
          </CardItem>
        </template>
        <template v-else-if="loading">
          <div class="template-item-empty">
            <div class="w-full pt-[56%] max-h-[220px] bg-gray-200 rounded-md"></div>
            <a-skeleton active :paragraph="{ rows: 1 }" />
          </div>
        </template>
      </div>
    </div>
    <EmptyView v-if="list.length === 0" :style="{ background: 'none' }" />
  </div>
</template>

<script setup lang="ts">
import type { Template } from '~/api/interface';
import EmptyView from '~/components/emptyView/index.vue';
import CardItem from '~/features/template/components/cardItem.vue';
import { getTagIDs } from '~/features/template/components/info';
import { useTemplateDetail } from '~/hooks/useModal/modals/template';
import { TEMPLATE_SOURCE, TRACK_EVENT } from '~/utils/track';

const props = withDefaults(
  defineProps<{
    list: Template[];
    loading?: boolean;
    template_source: TEMPLATE_SOURCE;
    onCardClick?: (data: Template) => void;
    mode?: 'free' | 'default';
  }>(),
  {
    loading: false,
  },
);

const computedList = computed(() => {
  let baseCount = 4;
  if (import.meta.client) {
    document.documentElement.clientWidth > 1920 && (baseCount = 5);
  }
  const filledListLength = Math.ceil(props.list.length / baseCount) * baseCount;
  const gap = filledListLength - props.list.length || baseCount;
  return [...props.list, ...new Array(gap).fill(null)];
});

const isVertical = computed(() => {
  return props.list[0]?.resolution === '1080x1920';
});

const onChange = ([key, value]: [string, any], data: Template) => {
  const item: any = props.list.find(item => {
    return item.id === data.id;
  });
  if (item) {
    item[key] = value;
  }
};

const resourceLocation = injectResourceLocation();
const userInfo = useUserInfo();

const onHandleCoverClick = (data: Template) => {
  navigateTo(`/template/${data.id}.html`);
};

const onHandlePreviewClick = (data: Template) => {
  const trackParams = {
    template_id: data.id,
    template_title: data.name,
    source: resourceLocation?.source,
    tags: getTagIDs(data.tag_ids).map(item => item.tag_name),
    org_id: userInfo.value?.organization_id,
  };

  if (props.template_source === TEMPLATE_SOURCE.BEST_PRACTICE_PAGE) {
    trackEvent(TRACK_EVENT.CLICK_EXCELLENT_WORK, trackParams);
  } else {
    trackEvent(TRACK_EVENT.CLICK_TEMPLATE_PREVIEW, trackParams);
  }

  useTemplateDetail(
    props.list,
    data,
    {
      templateSource: props.template_source,
      source: resourceLocation?.source,
    },
    onChange,
  );
};
</script>
<style lang="scss" scoped>
.templates {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;

  @media screen and (max-width: 1920px) {
    grid-template-columns: repeat(4, 1fr);
  }

  &.vertical {
    @media screen and (max-width: 1920px) {
      grid-template-columns: repeat(5, 1fr);
    }
  }
}
</style>
